import { debounce, formatMoney, fetchSkuSearchResults } from './bulk-order-api.js';

/** @typedef {import('./bulk-order-api.js').PredictiveProduct} PredictiveProduct */

const MIN_QUERY_LENGTH = 2;
const MAX_SEARCH_LIST = 100;
const DEBOUNCE_MS = 300;

class BulkOrderSearch extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null; // AbortController for the in-flight fetch
  #activeIndex = -1;
  /** @type {(((query: string) => void) & { cancel: () => void }) | null} */
  #onInput = null;

  /** @type {URL} */
  baseUrl;
  /** @type {HTMLInputElement} */
  input;
  /** @type {HTMLButtonElement} */
  clearButton;
  /** @type {HTMLButtonElement} */
  clearResultsButton;
  /** @type {HTMLElement} */
  panel;
  /** @type {HTMLElement} */
  panelHeader;
  /** @type {HTMLElement} */
  resultsList;
  /** @type {HTMLElement} */
  emptyMessage;
  /** @type {HTMLElement} */
  loadingMessage;
  /** @type {HTMLTemplateElement} */
  resultTemplate;
  /** @type {HTMLElement} */
  combobox;

  connectedCallback() {
    this.baseUrl = new URL(Theme.routes.search_url, location.origin);

    this.input = /** @type {HTMLInputElement} */ (this.querySelector('[data-search-input]'));
    this.clearButton = /** @type {HTMLButtonElement} */ (this.querySelector('[data-search-clear]'));
    this.clearResultsButton = /** @type {HTMLButtonElement} */ (this.querySelector('[data-search-clear-results]'));
    this.panel = /** @type {HTMLElement} */ (this.querySelector('[data-search-panel]'));
    this.panelHeader = /** @type {HTMLElement} */ (this.querySelector('[data-search-panel-header]'));
    this.resultsList = /** @type {HTMLElement} */ (this.querySelector('[data-search-results]'));
    this.emptyMessage = /** @type {HTMLElement} */ (this.querySelector('[data-search-empty]'));
    this.loadingMessage = /** @type {HTMLElement} */ (this.querySelector('[data-search-loading]'));
    this.resultTemplate = /** @type {HTMLTemplateElement} */ (this.querySelector('[data-search-result-template]'));
    this.combobox = /** @type {HTMLElement} */ (this.querySelector('[role="combobox"]'));

    this.#onInput = debounce((value) => this.#search(value), DEBOUNCE_MS);

    this.input.addEventListener('input', this.#handleInput);
    this.input.addEventListener('keydown', this.#handleKeydown);
    this.input.addEventListener('focus', this.#handleFocus);
    this.clearButton.addEventListener('click', this.#handleClear);
    this.clearResultsButton.addEventListener('click', this.#handleClear);
    this.resultsList.addEventListener('click', this.#handleResultClick);
    document.addEventListener('click', this.#handleOutsideClick);
  }

  disconnectedCallback() {
    this.#onInput?.cancel?.();
    this.#controller?.abort();
    this.input.removeEventListener('input', this.#handleInput);
    this.input.removeEventListener('keydown', this.#handleKeydown);
    this.input.removeEventListener('focus', this.#handleFocus);
    this.clearButton.removeEventListener('click', this.#handleClear);
    this.clearResultsButton.removeEventListener('click', this.#handleClear);
    this.resultsList.removeEventListener('click', this.#handleResultClick);
    document.removeEventListener('click', this.#handleOutsideClick);
  }

  /** @param {Event} event */
  #handleInput = (event) => {
    const value = /** @type {HTMLInputElement} */ (event.target).value;
    this.clearButton.hidden = value.length === 0;

    if (value.trim().length < MIN_QUERY_LENGTH) {
      this.#closePanel();
      return;
    }
    this.#onInput?.(value.trim());
  };

  #handleFocus = () => {
    if (this.input.value.trim().length >= MIN_QUERY_LENGTH && this.resultsList.children.length > 0) {
      this.#openPanel();
    }
  };

  #handleClear = () => {
    this.input.value = '';
    this.clearButton.hidden = true;
    this.#closePanel();
    this.input.focus();
  };

  /** @param {MouseEvent} event */
  #handleOutsideClick = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) {
      this.#closePanel();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const options = Array.from(this.resultsList.querySelectorAll('[data-search-result]'));
    if (!options.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#setActiveIndex(Math.min(this.#activeIndex + 1, options.length - 1), options);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#setActiveIndex(Math.max(this.#activeIndex - 1, 0), options);
        break;
      case 'Enter':
        if (this.#activeIndex >= 0) {
          event.preventDefault();
          this.#selectResult(/** @type {HTMLElement} */ (options[this.#activeIndex]));
        }
        break;
      case 'Escape':
        this.#closePanel();
        break;
      default:
        break;
    }
  };

  /**
   * @param {number} index
   * @param {Element[]} options
   */
  #setActiveIndex(index, options) {
    options.forEach((option) => option.classList.remove('is-active'));
    this.#activeIndex = index;
    const active = options[index];
    if (active) {
      active.classList.add('is-active');
      active.scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', active.id);
    }
  }

  /** @param {MouseEvent} event */
  #handleResultClick = (event) => {
    const result = /** @type {HTMLElement} */ (event.target).closest('[data-search-result]');
    if (result) this.#selectResult(/** @type {HTMLElement} */ (result));
  };

  /** @param {HTMLElement} resultEl */
  #selectResult(resultEl) {
    /** @type {ReturnType<typeof JSON.parse>} */
    let detail;
    try {
      detail = JSON.parse(/** @type {string} */ (resultEl.dataset.payload));
    } catch (error) {
      console.error('[bulk-order] invalid search result payload', error);
      return;
    }

    document.dispatchEvent(
      new CustomEvent('bulk-order:product-selected', {
        bubbles: true,
        detail,
      })
    );
    this.#handleClear();
  }

  /** @param {string} query */
  async #search(query) {
    this.#controller?.abort();
    this.#controller = new AbortController();

    this.#openPanel();
    this.#setLoading(true);

    try {
      const products = await fetchSkuSearchResults(this.baseUrl, query, {
        signal: /** @type {AbortController} */ (this.#controller).signal,
      });
      this.#renderResults(products, query);
    } catch (error) {
      if (error.name === 'AbortError') return; // superseded by a newer keystroke
      console.error('[bulk-order] search failed', error);
      this.#renderResults([], query, /* isError */ true);
    } finally {
      this.#setLoading(false);
    }
  }

  /**
   * @param {PredictiveProduct[]} products
   * @param {string} query
   * @param {boolean} [isError]
   */
  #renderResults(products, query, isError = false) {
    this.resultsList.innerHTML = '';
    this.resultsList.scrollTop = 0;

    this.#activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');

    const variants = products
      .flatMap((product) =>
        (product.variants || []).map((variant) => ({ product, variant }))
      );

    this.panelHeader.hidden = variants.length === 0;
    this.emptyMessage.hidden = variants.length !== 0 || isError;
    if (isError) {
      this.emptyMessage.hidden = false;
      this.emptyMessage.textContent = this.emptyMessage.dataset.errorText || this.emptyMessage.textContent;
    }

    variants.slice(0, MAX_SEARCH_LIST).forEach(({ product, variant }, index) => {
      const fragment = /** @type {DocumentFragment} */ (this.resultTemplate.content.cloneNode(true));
      const li = /** @type {HTMLElement} */ (fragment.querySelector('[data-search-result]'));
      li.id = `bulk-order-result-${index}`;

      const title = variant.title && variant.title !== 'Default Title' ? `${product.title} - ${variant.title}` : product.title;
      /** @type {HTMLElement} */ (fragment.querySelector('[data-result-title]')).textContent = title;
      /** @type {HTMLElement} */ (fragment.querySelector('[data-result-price]')).textContent = formatMoney(
        variant.price,
        /** @type {HTMLElement | null} */ (this.closest('bulk-order-workspace'))?.dataset.moneyFormat
      );

      const media = /** @type {HTMLElement} */ (fragment.querySelector('[data-result-media]'));
      const imageUrl = product.featured_image?.url || product.image;
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = '';
        img.loading = 'lazy';
        media.appendChild(img);
      }

      li.dataset.payload = JSON.stringify({
        sku: variant.sku,
        variantId: variant.id,
        productId: product.id,
        title: product.title,
        variantTitle: variant.title !== 'Default Title' ? variant.title : null,
        price: variant.price,
        image: product.featured_image?.url || product.image || null,
        available: variant.available !== false,
        url: product.url,
      });

      this.resultsList.appendChild(fragment);
    });

  }

  /** @param {boolean} isLoading */
  #setLoading(isLoading) {
    this.loadingMessage.hidden = !isLoading;
    this.resultsList.hidden = isLoading;
    if (isLoading) {
      this.panelHeader.hidden = true;
      this.emptyMessage.hidden = true;
    }
  }

  #openPanel() {
    this.panel.hidden = false;
    this.combobox.setAttribute('aria-expanded', 'true');
  }

  #closePanel() {
    this.panel.hidden = true;
    this.combobox.setAttribute('aria-expanded', 'false');
    this.#activeIndex = -1;
  }
}

customElements.define('bulk-order-search', BulkOrderSearch);
